import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import firebase from "firebase/app";
import {
  firebaseConfig,
  createAdminProfileDocument,
} from "../../firebase/firebase.utils";
import Breadcrumb from "../common/breadcrumb";
import AdminBadge from "../admins/AdminBadge";
import "../admins/admins-kido.css";
import { UserPlus, CheckCircle, Copy, Check } from "react-feather";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { notifyWarning, notifyError } from "../../utils/notify";

class RegisterAdmin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displayName: "",
      email: "",
      password: "",
      confirmPassword: "",
      status: "",
      loading: false,
      createdCredentials: null, // { name, email, password, role } shown after success
      copied: false,
    };
  }

  handleChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { displayName, email, password, confirmPassword, status } = this.state;

    if (password !== confirmPassword) {
      notifyWarning("Passwords don't match.");
      return;
    }

    this.setState({ loading: true });

    // Use a secondary Firebase app so the current super admin session is NOT affected.
    // createUserWithEmailAndPassword would otherwise sign in as the new user immediately.
    let secondaryApp;
    try {
      const appName = `reg-${Date.now()}`;
      secondaryApp = firebase.apps.find((a) => a.name === appName)
        || firebase.initializeApp(firebaseConfig, appName);

      const secondaryAuth = secondaryApp.auth();
      const { user } = await secondaryAuth.createUserWithEmailAndPassword(email, password);
      await createAdminProfileDocument(user, { name: displayName, status });
      await secondaryAuth.signOut();
      await secondaryApp.delete();

      this.setState({
        displayName: "",
        email: "",
        password: "",
        confirmPassword: "",
        status: "",
        loading: false,
        createdCredentials: { name: displayName, email, password, role: status },
        copied: false,
      });
      toast.success(`Account created for ${displayName}`);
    } catch (error) {
      if (secondaryApp) {
        try { await secondaryApp.delete(); } catch (_) {}
      }
      notifyError(error.message);
      this.setState({ loading: false });
    }
  };

  handleCopy = () => {
    const { createdCredentials } = this.state;
    const text = `Name: ${createdCredentials.name}\nEmail: ${createdCredentials.email}\nPassword: ${createdCredentials.password}\nRole: ${createdCredentials.role}`;
    navigator.clipboard.writeText(text).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2500);
    });
  };

  render() {
    const { displayName, email, password, confirmPassword, status, loading, createdCredentials, copied } = this.state;
    const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

    return (
      <div>
        <Breadcrumb title="Register Admin" parent="Admins" />
        <div className="container-fluid">
          <section className="card kd-orders-card kd-admins">
            <div className="kd-ord-top">
              <div className="kd-ord-title">
                <span className="kd-ord-title-tile kd-st--lilac" aria-hidden="true">
                  <UserPlus size={20} />
                </span>
                <div>
                  <h2>{createdCredentials ? "Register another admin" : "Register an admin"}</h2>
                  <svg className="kd-squiggle" viewBox="0 0 120 10" aria-hidden="true" focusable="false">
                    <path d="M2 6 Q12 1 22 6 T42 6 T62 6 T82 6 T102 6 T118 5" />
                  </svg>
                </div>
              </div>
            </div>

            {/* ── Credentials shown after a successful registration ── */}
            {createdCredentials && (
              <div className="kd-reg-done" role="status">
                <div className="kd-reg-done-head">
                  <h3>
                    <CheckCircle size={18} aria-hidden="true" />
                    Account created for {createdCredentials.name}
                  </h3>
                  <button type="button" className="btn btn-secondary" onClick={this.handleCopy}>
                    {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                    {copied ? "Copied" : "Copy sign-in details"}
                  </button>
                </div>
                <dl className="kd-reg-creds">
                  <CredRow label="Name" value={createdCredentials.name} />
                  <CredRow label="Email" value={createdCredentials.email} />
                  <CredRow label="Password" value={createdCredentials.password} secret />
                  <CredRow label="Role" value={createdCredentials.role} />
                </dl>
                <p>Share these with the new admin. They disappear when you register another account.</p>
              </div>
            )}

            <div className="kd-reg">
              <form className="kd-reg-form" onSubmit={this.handleSubmit}>
                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label htmlFor="kd-reg-name">Full name</label>
                    <input
                      id="kd-reg-name"
                      className="form-control"
                      name="displayName"
                      value={displayName}
                      onChange={this.handleChange}
                      type="text"
                      autoComplete="off"
                      required
                    />
                  </div>
                  <div className="form-group col-md-6">
                    <label htmlFor="kd-reg-role">Role</label>
                    <select
                      id="kd-reg-role"
                      className="form-control"
                      name="status"
                      value={status}
                      onChange={this.handleChange}
                      required
                    >
                      <option value="">Select role</option>
                      <option value="Admin">Admin (Super)</option>
                      <option value="Accounts">Accounts</option>
                      <option value="Employee">Employee</option>
                      <option value="Officer">Officer</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-12">
                    <label htmlFor="kd-reg-email">Email address</label>
                    <input
                      id="kd-reg-email"
                      className="form-control"
                      name="email"
                      value={email}
                      onChange={this.handleChange}
                      type="email"
                      autoComplete="off"
                      required
                    />
                    <span className="kd-reg-hint">They'll use this to sign in.</span>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label htmlFor="kd-reg-pass">Password</label>
                    <input
                      id="kd-reg-pass"
                      className="form-control"
                      name="password"
                      value={password}
                      onChange={this.handleChange}
                      type="password"
                      autoComplete="new-password"
                      required
                      minLength={6}
                    />
                    <span className="kd-reg-hint">At least 6 characters.</span>
                  </div>
                  <div className="form-group col-md-6">
                    <label htmlFor="kd-reg-confirm">Confirm password</label>
                    <input
                      id="kd-reg-confirm"
                      className={"form-control" + (mismatch ? " is-mismatch" : "")}
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={this.handleChange}
                      type="password"
                      autoComplete="new-password"
                      aria-invalid={mismatch}
                      required
                    />
                    {mismatch && <span className="kd-reg-hint is-bad">Passwords don't match yet.</span>}
                  </div>
                </div>

                <div className="kd-reg-submit">
                  <p>You stay signed in while the account is created.</p>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? "Creating account…" : "Create account"}
                  </button>
                </div>
              </form>

              <aside className="kd-reg-preview" aria-label="Badge preview">
                <span className="kd-reg-preview-label">Their badge</span>
                <AdminBadge
                  preview
                  admin={{ name: displayName, email, status, activeStatus: "Active" }}
                />
              </aside>
            </div>
          </section>
        </div>
        <ToastContainer />
      </div>
    );
  }
}

function CredRow({ label, value, secret }) {
  const [show, setShow] = React.useState(false);
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        {secret && !show ? "••••••••" : value}
        {secret && (
          <button type="button" onClick={() => setShow(!show)}>
            {show ? "Hide" : "Show"}
          </button>
        )}
      </dd>
    </div>
  );
}

const mapStateToProps = (state) => ({
  currentAdmin: state.admins.currentAdmin,
});

export default connect(mapStateToProps)(withRouter(RegisterAdmin));
